import React, { FC, useEffect, useState } from 'react';
import { Box, Divider, LinearProgress, Link, Tooltip, Typography, useTheme } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import { constants } from '../../constants/constants';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import PlatformService from '../../services/PlatformService';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { IGMBLocationsResponse } from '../../shared/interfaces/IGMBLocationsResponse';
import GMBService from '../../services/GMBService';
import { IScoreEntry } from '../../shared/interfaces/IScoreEntry';
import ScoreService from '../../services/ScoreService';
import { WidgetNoData } from './WidgetNoData';
import { useTranslation } from 'react-i18next';

const WidgetRatingList: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [lastTotalScores, setLastTotalScores] = useState<IScoreEntry[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };
    const getMonitoring = async () => {
      let monitoringEntries: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'both',
        setLoading,
        controller.signal
      );

      if (!monitoringEntries) return;

      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      for (const location of gmbMonitoredLocations.locations) {
        const mon: IMonitoring = {
          company: location.locationID,
          createdAt: '',
          deletedAt: '',
          id: location.ID,
          jobs: [],
          platformId: -2,
          label: location.locationData.title,
          url: '',
          updatedAt: '',
          communityManagement: location.communityManagement,
          isBenchmark: false,
          type: 'LOCATION',
        };
        monitoringEntries.push(mon);
      }

      if (hasGmbEntries) {
        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      setMonitoring(monitoringEntries);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const getLastTotalScores = async (monitoring: IMonitoring[]) => {
      let scoreEntries: IScoreEntry[] = [];
      for (const value of monitoring) {
        const platformName = PlatformService.getPlatformName(value.platformId, platforms);
        const lastScores = await ScoreService.getLastScores(
          platformName,
          value.company,
          setLoading,
          controller.signal
        );

        if (!Array.isArray(lastScores)) return;
        for (const lastScore of lastScores) {
          if (!lastScore.kind || lastScore.kind === 'total') {
            scoreEntries.push(lastScore);
          }
        }
      }
      setLastTotalScores(scoreEntries);
    };

    if (monitoring.length > 0) {
      (async () => await getLastTotalScores(monitoring))();
    }

    return () => {
      controller.abort();
    };
  }, [monitoring, platforms]);

  const sortedScores = lastTotalScores
    .sort((a, b) => (b.score.reviewCount || 0) - (a.score.reviewCount || 0))
    .slice(0, 4);

  const cutLabel = (label: string) => {
    return label.length > 20 ? label.substring(0, 20) + '...' : label;
  };

  return (
    <Box
      sx={{
        maxHeight: '290px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: '290px',
          }}
        >
          {sortedScores.length > 0 ? (
            sortedScores.map((scores: IScoreEntry, index: number) => {
              const matchingMonitoring = monitoring.find((mon) => mon.company === scores.company);

              return (
                <Box
                  key={`${scores.id}-${index}`}
                  sx={{ m: theme.spacing(1) }}
                >
                  <Divider sx={{ mb: theme.spacing(1) }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ width: '90px' }}>
                          <img
                            style={{ height: '40px', width: '80px', objectFit: 'contain' }}
                            alt={t('platform_logo')}
                            src={constants.platform_logos[scores.platform]}
                          />
                        </Box>
                        {matchingMonitoring && (
                          <Box sx={{ width: '120px' }}>
                            <Link
                              href={
                                scores.platform === 'googleplace'
                                  ? `https://www.google.com/maps/place/?q=place_id:${matchingMonitoring.company}`
                                  : matchingMonitoring.url
                              }
                              variant="body2"
                              underline={'none'}
                              target="_blank"
                              sx={{
                                cursor: 'pointer',
                                color:
                                  theme.palette.mode === 'dark'
                                    ? 'white'
                                    : theme.palette.primary.main,
                              }}
                            >
                              {cutLabel(matchingMonitoring.label)}
                            </Link>
                          </Box>
                        )}
                        <Box sx={{ width: '120px' }}>
                          <StarRating value={scores.score.score} />
                        </Box>
                        <Box sx={{ width: '50px' }}>
                          <Tooltip
                            title={
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2">{t('percentage_score_ytd')}</Typography>
                              </Box>
                            }
                            arrow
                          >
                            <span>{scores.growthPercentage}%</span>
                          </Tooltip>
                        </Box>
                        <Box sx={{ width: '30px' }}>
                          <GrowthIndicator growth={scores.growthPercentage} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          ) : (
            <>
              <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
              <WidgetNoData />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WidgetRatingList;
